import * as Sentry from '@sentry/react';
import { tabFlowId } from 'dg-web-shared/lib/FlowId';
import { withBackendRequestId } from 'dg-web-shared/lib/SentryRequestId';
import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';

import {
    envIsLocalDev,
    envIsProduction,
    envIsTest,
} from 'dg-web-shared/lib/Environment.ts';
import { isHostInjectedScriptNoise } from 'dg-web-shared/lib/SentryEventFilter.ts';

const dsn = getSentryProjectDSN();
if (dsn != null) {
    Sentry.init({
        dsn: dsn,
        attachStacktrace: true,
        initialScope: { tags: { service: 'customer-app' } },
        beforeBreadcrumb: withBackendRequestId,
        tunnel: '/ui-api/sentry-tunnel',
        allowUrls: [
            /https?:\/\/parkingpay\.ch/,
            /.*\.digitalparking\.ch/,
            /.*\.digitalparking\.dev/,
        ],
        // Legacy noise list, copied wholesale from the errorception blog post
        // linked below in the early 2010s. Entries are matched as SUBSTRINGS
        // against both `value` and `${type}: ${value}`, so they are blunt
        // instruments that can silently swallow our own bugs. Never add an
        // entry without a date and a reason for it being here.
        //
        // 2026-08-20: removed 'NotFoundError: The object can not be found here.'
        // from this list. That is the WebKit/iOS wording of
        // "NotFoundError: Failed to execute 'removeChild' on 'Node'", which is
        // React choking on DOM nodes that Chrome-for-iOS page translation
        // rewrote underneath it (Sentry CUSTOMER-PROD-DWM / CUSTOMER-PROD-D45).
        // Ignoring it dropped the entire iOS share of that crash before ingest
        // and made the platform mix look biased away from iOS - the exact
        // platform where the translation happens.
        ignoreErrors: [
            // Random plugins/extensions
            'top.GLOBALS',
            // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'http://tt.epicplay.com',
            "Can't find variable: ZiteReader",
            'jigsaw is not defined',
            'ComboSearch is not defined',
            'http://loading.retry.widdit.com/',
            'atomicFindClose',
            // Facebook borked
            'fb_xd_fragment',
            // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
            // reduce this. (thanks @acdha)
            // See http://stackoverflow.com/questions/4113268
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
            'conduitPage',
            // 2026-08-20: undated leftover of the same copy-paste. Believed to
            // be a browser extension injecting a `gmo` global; we have no
            // record of it ever matching one of our own errors, and the
            // WebKit-only wording keeps it narrow enough to leave in place.
            "Can't find variable: gmo",
            // 2026-08-20: undated leftover of the same copy-paste, and the
            // broadest entry left - it also matches genuine JSON/stream parse
            // failures of our own. Kept for now, but it is the next candidate
            // for removal if we ever chase a "silent" parsing bug.
            'Unexpected EOF',
        ],
        denyUrls: [
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
        integrations: [
            Sentry.browserSessionIntegration({ lifecycle: 'page' }),
            Sentry.reactRouterBrowserTracingIntegration({
                useEffect,
                createRoutesFromChildren,
                matchRoutes,
                useLocation,
                useNavigationType,
            }),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: ['customer-prod'],
                behaviour:
                    'drop-error-if-exclusively-contains-third-party-frames',
            }),
            Sentry.captureConsoleIntegration({
                levels: ['error'],
            }),
            Sentry.browserApiErrorsIntegration({}),
            Sentry.globalHandlersIntegration(),
        ],
        beforeSend: event => (isHostInjectedScriptNoise(event) ? null : event),
    });
    // sets the flow_id tag before the first request
    tabFlowId();
}

function getSentryProjectDSN(): string | null {
    if (envIsProduction()) {
        return 'https://057c998e20294992adc94b599fad98f7@o28142.ingest.sentry.io/59593';
    } else if (envIsTest()) {
        return 'https://8e49437142f14d94bd1fa159475b078a@o28142.ingest.sentry.io/59592';
    } else if (envIsLocalDev()) {
        return null;
    } else {
        return 'https://9d6d1bcd0b974c138a12a4ab7335ddbf@app.getsentry.com/59458';
    }
}
